import { useMemo } from 'react';
import type { HeightmapAsset, ParamSchema, ParamValue, ParamValues } from '@/core/types';
import { AngleDial, Field, Select, Slider, Toggle } from './Controls';

/**
 * Controls generated from a parameter schema.
 *
 * This is where the registry design pays off: a new pattern declares its
 * parameters and gets a complete, validated, grouped control panel without a
 * line of UI code. Nothing here knows what a wave or a pyramid is.
 */

interface ParamControlsProps {
  schema: readonly ParamSchema[];
  values: ParamValues;
  /** Panel pitch in millimetres, used to show what a span works out to. */
  pitchMm?: number;
  assets?: readonly HeightmapAsset[];
  onChange(key: string, value: ParamValue): void;
  onRequestImage?(): void;
}

export function ParamControls({
  schema,
  values,
  pitchMm,
  assets,
  onChange,
  onRequestImage,
}: ParamControlsProps) {
  // Preserve declaration order while collecting each group's members.
  const groups = useMemo(() => {
    const ordered = new Map<string, ParamSchema[]>();
    for (const param of schema) {
      const key = param.group ?? '';
      const existing = ordered.get(key);
      if (existing) existing.push(param);
      else ordered.set(key, [param]);
    }
    return [...ordered.entries()];
  }, [schema]);

  return (
    <div className="params">
      {groups.map(([group, params]) => (
        <div className="params__group" key={group || 'default'}>
          {group ? <h4 className="params__group-title">{group}</h4> : null}
          {params.map((param) => (
            <ParamControl
              key={param.key}
              param={param}
              value={values[param.key]}
              pitchMm={pitchMm}
              assets={assets}
              onChange={onChange}
              onRequestImage={onRequestImage}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

interface ParamControlProps {
  param: ParamSchema;
  value: ParamValue | undefined;
  pitchMm?: number;
  assets?: readonly HeightmapAsset[];
  onChange(key: string, value: ParamValue): void;
  onRequestImage?(): void;
}

function ParamControl({
  param,
  value,
  pitchMm,
  assets,
  onChange,
  onRequestImage,
}: ParamControlProps) {
  switch (param.kind) {
    case 'number':
      return (
        <Slider
          label={param.label}
          help={param.help}
          value={typeof value === 'number' ? value : param.default}
          min={param.min}
          max={param.max}
          step={param.step}
          unit={param.unit}
          onChange={(next) => onChange(param.key, next)}
        />
      );

    case 'int':
      return (
        <Slider
          label={param.label}
          help={param.help}
          value={typeof value === 'number' ? value : param.default}
          min={param.min}
          max={param.max}
          step={1}
          onChange={(next) => onChange(param.key, Math.round(next))}
        />
      );

    case 'span': {
      const current = typeof value === 'number' ? value : param.default;
      // Showing the millimetre equivalent makes "in panels" concrete without
      // taking away the property that keeps a motif aligned to the joints.
      const millimetres = pitchMm ? ` · ${(current * pitchMm).toFixed(0)} mm` : '';
      return (
        <Slider
          label={param.label}
          help={`${param.help ?? ''}${param.help ? ' ' : ''}Measured in panels${millimetres}.`}
          value={current}
          min={param.min}
          max={param.max}
          step={param.step}
          unit="panels"
          onChange={(next) => onChange(param.key, next)}
        />
      );
    }

    case 'angle':
      return (
        <AngleDial
          label={param.label}
          help={param.help}
          value={typeof value === 'number' ? value : param.default}
          onChange={(next) => onChange(param.key, next)}
        />
      );

    case 'boolean':
      return (
        <Toggle
          label={param.label}
          help={param.help}
          checked={typeof value === 'boolean' ? value : param.default}
          onChange={(next) => onChange(param.key, next)}
        />
      );

    case 'select':
      return (
        <Select
          label={param.label}
          help={param.help}
          value={typeof value === 'string' ? value : param.default}
          options={param.options}
          onChange={(next) => onChange(param.key, next)}
        />
      );

    case 'asset': {
      const current = typeof value === 'string' ? value : param.default;
      const available = assets ?? [];

      return (
        <Field label={param.label} help={param.help}>
          {available.length === 0 ? (
            <button type="button" className="button button--ghost" onClick={onRequestImage}>
              Import an image…
            </button>
          ) : (
            <div className="asset-picker">
              {available.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  className={`asset-chip${asset.id === current ? ' is-active' : ''}`}
                  onClick={() => onChange(param.key, asset.id)}
                  title={`${asset.name} — ${asset.width}x${asset.height}`}
                >
                  {asset.name}
                </button>
              ))}
              <button type="button" className="asset-chip asset-chip--add" onClick={onRequestImage}>
                + Add
              </button>
            </div>
          )}
        </Field>
      );
    }
  }
}
